import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BrainCircuit, Plus } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Select } from "@/components/ui/Select";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import { canAccessContractorKnowledge, getContractorDisplayName, type ContractorLike } from "../utils";
import {
  EFFECT_OPTIONS,
  SCOPE_OPTIONS,
  useContractorKnowledgeNotes,
  useKnowledgeNoteMutations,
  type KnowledgeEffect,
} from "../api/knowledgeNotes";
import { KnowledgeNoteForm } from "../components/KnowledgeNoteForm";
import { KnowledgeNotesList } from "../components/KnowledgeNotesList";

/**
 * Rejestr zbiorczy „Zasad podpowiadania podwykonawców" — wszystkie zasady (globalne i
 * per firma) z filtrami; odpowiednik strony „Baza wiedzy PRZ-ARON" dla zamówień. Trasa
 * jest pod ContractorsManageGuard (krąg „Podwykonawcy i umowy ramowe": CEO, dyrektor
 * techniczny, flaga) — jak bramka backendu CanManageContractors.
 */

interface ContractorRow extends ContractorLike {
  id: string;
}

export function KnowledgeNotesPage() {
  usePageTitle("Zasady podpowiadania podwykonawców");
  const user = useAuthStore((s) => s.user);
  const canManage = canAccessContractorKnowledge(user);
  const { create } = useKnowledgeNoteMutations();

  const [scope, setScope] = useState("");
  const [effect, setEffect] = useState<KnowledgeEffect | "">("");
  const [contractor, setContractor] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [adding, setAdding] = useState(false);

  const filters = useMemo(
    () => ({
      ...(scope ? { scope } : {}),
      ...(effect ? { effect } : {}),
      ...(contractor === "__global__" ? { is_global: true } : contractor ? { contractor } : {}),
      ...(onlyActive ? { is_active: true } : {}),
    }),
    [scope, effect, contractor, onlyActive],
  );
  const notesQ = useContractorKnowledgeNotes(filters);

  const contractorsQ = useQuery({
    queryKey: ["contractors", "active"],
    queryFn: () =>
      apiClient
        .get<{ data: ContractorRow[] }>("/contractors/", { params: { is_active: "true" } })
        .then((r) => r.data.data),
  });
  const contractorOptions = useMemo(
    () => [
      { value: "__global__", label: "— tylko zasady globalne —" },
      ...(contractorsQ.data ?? [])
        .map((c) => ({ value: c.id, label: getContractorDisplayName(c) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [contractorsQ.data],
  );

  const notes = notesQ.data ?? [];
  const inForce = notes.filter((n) => n.in_force).length;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--color-text-primary)]">
          <BrainCircuit size={18} className="text-[var(--color-text-muted)]" />
          Zasady podpowiadania podwykonawców
          {notesQ.data && (
            <span className="text-sm font-normal text-[var(--color-text-secondary)]">
              {inForce} obowiązujących / {notes.length}
            </span>
          )}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          Wskazówki w języku naturalnym, które system uwzględnia, podpowiadając podwykonawcę
          przy zlecaniu (zaproszenia do postępowań etapów, zlecenia WPW / MDCP / PT / kosztorysu
          / Wód Polskich, zwykłe zapotrzebowania) — tak jak Baza wiedzy PRZ-ARON w wycenach.
          Zasadę można przypisać do konkretnej firmy, rodzaju zlecenia, przedsięwzięcia i okna
          dat. „Wskazówka dla AI” jest oceniana przez model na faktach (historia zleceń na
          projekcie, czasy w godzinach); „Nie podpowiadaj” / „Podpowiadaj zawsze” działają
          twardo, również bez AI. Żadna zasada nie blokuje ręcznego wyboru firmy. Zasadę można
          wyłączyć przełącznikiem — zostaje zapisana, ale nie wpływa na podpowiedzi, dopóki
          ktoś jej nie włączy.
        </p>
      </div>

      {canManage && (
        <div className="card space-y-2 p-4">
          {adding ? (
            <KnowledgeNoteForm
              pending={create.isPending}
              error={create.isError ? "Nie udało się zapisać zasady." : null}
              onCancel={() => setAdding(false)}
              onSubmit={(payload) => create.mutate(payload, { onSuccess: () => setAdding(false) })}
            />
          ) : (
            <Button onClick={() => setAdding(true)}>
              <Plus size={14} /> Nowa zasada
            </Button>
          )}
        </div>
      )}

      <div className="card space-y-2 p-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Select
            label="Rodzaj zlecenia"
            options={[
              { value: "", label: "Wszystkie" },
              { value: "any", label: "Tylko bez rodzaju (każde zlecenie)" },
              ...SCOPE_OPTIONS.filter((o) => o.value).map((o) => ({ value: o.value, label: o.label })),
            ]}
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          />
          <Select
            label="Działanie"
            options={[
              { value: "", label: "Wszystkie" },
              ...EFFECT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            ]}
            value={effect}
            onChange={(e) => setEffect(e.target.value as KnowledgeEffect | "")}
          />
          <SearchableSelect
            label="Podwykonawca"
            placeholder="— wszystkie firmy —"
            options={contractorOptions}
            value={contractor}
            onChange={(e) => setContractor(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
          <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />
          Tylko aktywne
        </label>
      </div>

      <div className="card px-4">
        {notesQ.isLoading ? (
          <p className="py-3 text-sm text-[var(--color-text-secondary)]">Wczytywanie…</p>
        ) : (
          <KnowledgeNotesList
            notes={notes}
            canManage={canManage}
            showContractor
            emptyText="Brak zasad — dodaj pierwszą powyżej albo na karcie podwykonawcy."
          />
        )}
      </div>
    </div>
  );
}
